import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateSpotInput, ParkingDto, SpotDetailDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { useRealtime } from '../shared/realtime/useRealtime';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';
import { SpotFloorPlanCanvas, type SpotMapPose } from '../shared/ui/SpotFloorPlanCanvas';

type ParkingForm = {
  code: string;
  name: string;
  address: string;
  city: string;
  floorPlanBase64: string | null;
  floorPlanPreview: string | null;
  clearFloorPlan: boolean;
};
type SpotForm = { code: string; type: string; status: string; labelColor: string };

const emptyParking: ParkingForm = {
  code: '',
  name: '',
  address: '',
  city: '',
  floorPlanBase64: null,
  floorPlanPreview: null,
  clearFloorPlan: false,
};
const emptySpot: SpotForm = {
  code: '',
  type: 'STANDARD',
  status: 'AVAILABLE',
  labelColor: '#FFFFFF',
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.readAsDataURL(file);
  });
}

export function ParkingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('parkings:read'));
  const canWriteParking = useAuthStore((s) => s.hasPermission('parkings:write'));
  const canWriteSpot = useAuthStore((s) => s.hasPermission('spots:write'));
  const canReadSessions = useAuthStore((s) => s.hasPermission('sessions:read'));

  const [selectedId, setSelectedId] = useState<string>('');
  const [parkingDialog, setParkingDialog] = useState(false);
  const [spotDialog, setSpotDialog] = useState(false);
  const [editingParkingId, setEditingParkingId] = useState<string | null>(null);
  const [editingSpotId, setEditingSpotId] = useState<string | null>(null);
  const [parkingForm, setParkingForm] = useState<ParkingForm>(emptyParking);
  const [spotForm, setSpotForm] = useState<SpotForm>(emptySpot);
  const [formError, setFormError] = useState<string | null>(null);

  const parkings = useQuery({
    queryKey: ['parkings'],
    queryFn: () => api.listParkings(),
    enabled: canRead,
  });

  const activeParkingId = selectedId || parkings.data?.data[0]?.id || '';

  const spots = useQuery({
    queryKey: ['spots', activeParkingId],
    queryFn: () => api.listSpots(activeParkingId),
    enabled: canRead && Boolean(activeParkingId),
  });

  const { lastOccupancy } = useRealtime();
  useEffect(() => {
    if (!lastOccupancy) return;
    if (lastOccupancy.parkingId && lastOccupancy.parkingId !== activeParkingId) return;
    void qc.invalidateQueries({ queryKey: ['spots', activeParkingId] });
    void qc.invalidateQueries({ queryKey: ['parkings'] });
  }, [lastOccupancy, activeParkingId, qc]);

  const sessions = useQuery({
    queryKey: ['sessions-open'],
    queryFn: () => api.listOpenSessions(),
    enabled: canReadSessions,
  });

  const closeParkingDialog = () => {
    setParkingDialog(false);
    setEditingParkingId(null);
    setParkingForm(emptyParking);
    setFormError(null);
  };

  const closeSpotDialog = () => {
    setSpotDialog(false);
    setEditingSpotId(null);
    setSpotForm(emptySpot);
    setFormError(null);
  };

  const openCreateParking = () => {
    setFormError(null);
    setEditingParkingId(null);
    setParkingForm(emptyParking);
    setParkingDialog(true);
  };

  const openEditParking = (p: ParkingDto) => {
    setFormError(null);
    setEditingParkingId(p.id);
    setParkingForm({
      code: p.code,
      name: p.name,
      address: p.address,
      city: p.city ?? '',
      floorPlanBase64: null,
      floorPlanPreview: p.floorPlanUrl ?? null,
      clearFloorPlan: false,
    });
    setParkingDialog(true);
  };

  const onFloorPlanSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError(t('parkings.floorPlanInvalid'));
      return;
    }
    if (file.size > 6_000_000) {
      setFormError(t('parkings.floorPlanTooLarge'));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setParkingForm((f) => ({
        ...f,
        floorPlanBase64: dataUrl,
        floorPlanPreview: dataUrl,
        clearFloorPlan: false,
      }));
      setFormError(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('parkings.floorPlanInvalid'));
    }
  };

  const openCreateSpot = () => {
    setFormError(null);
    setEditingSpotId(null);
    setSpotForm(emptySpot);
    setSpotDialog(true);
  };

  const openEditSpot = (s: SpotDetailDto) => {
    setFormError(null);
    setEditingSpotId(s.id);
    setSpotForm({
      code: s.code,
      type: s.type,
      status: s.status,
      labelColor: s.color && /^#[0-9A-Fa-f]{6}$/.test(s.color) ? s.color : '#FFFFFF',
    });
    setSpotDialog(true);
  };

  const saveParking = useMutation({
    mutationFn: async () => {
      const payload: {
        code: string;
        name: string;
        address: string;
        city?: string;
        floorPlanBase64?: string | null;
      } = {
        code: parkingForm.code.trim(),
        name: parkingForm.name.trim(),
        address: parkingForm.address.trim(),
        city: parkingForm.city.trim() || undefined,
      };
      if (parkingForm.floorPlanBase64) {
        payload.floorPlanBase64 = parkingForm.floorPlanBase64;
      } else if (editingParkingId && parkingForm.clearFloorPlan) {
        payload.floorPlanBase64 = null;
      }
      if (editingParkingId) {
        return api.updateParking(editingParkingId, payload);
      }
      return api.createParking(payload);
    },
    onSuccess: async (res) => {
      closeParkingDialog();
      await qc.invalidateQueries({ queryKey: ['parkings'] });
      setSelectedId(res.data.id);
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteParking = useMutation({
    mutationFn: (id: string) => api.deleteParking(id),
    onSuccess: async () => {
      setSelectedId('');
      await qc.invalidateQueries({ queryKey: ['parkings'] });
    },
  });

  const saveSpot = useMutation({
    mutationFn: async () => {
      const payload = {
        code: spotForm.code.trim(),
        type: spotForm.type as NonNullable<CreateSpotInput['type']>,
        status: spotForm.status as NonNullable<CreateSpotInput['status']>,
        color: spotForm.labelColor,
      };
      if (editingSpotId) {
        return api.updateSpot(editingSpotId, payload);
      }
      return api.createSpot({
        parkingId: activeParkingId,
        ...payload,
        mapX: 50,
        mapY: 50,
        mapRotation: 0,
        mapScale: 1,
      });
    },
    onSuccess: async () => {
      closeSpotDialog();
      await qc.invalidateQueries({ queryKey: ['spots', activeParkingId] });
      await qc.invalidateQueries({ queryKey: ['parkings'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const updateSpotPose = useMutation({
    mutationFn: (pose: SpotMapPose) =>
      api.updateSpot(pose.id, {
        mapX: pose.mapX,
        mapY: pose.mapY,
        mapRotation: pose.mapRotation,
        mapScale: pose.mapScale,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['spots', activeParkingId] });
    },
  });

  const deleteSpot = useMutation({
    mutationFn: (id: string) => api.deleteSpot(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['spots', activeParkingId] });
      await qc.invalidateQueries({ queryKey: ['parkings'] });
    },
  });

  const spotList = spots.data?.data ?? [];
  const parkingRows = parkings.data?.data ?? [];
  const sessionRows = sessions.data?.data ?? [];
  const activeParking = useMemo(
    () => parkingRows.find((p) => p.id === activeParkingId) ?? null,
    [parkingRows, activeParkingId],
  );

  function placeUnplacedSpot(mapX: number, mapY: number) {
    if (!canWriteSpot || !activeParkingId) return;
    const unplaced = spotList.find((s) => s.mapX == null || s.mapY == null);
    if (unplaced) {
      updateSpotPose.mutate({
        id: unplaced.id,
        mapX,
        mapY,
        mapRotation: unplaced.mapRotation ?? 0,
        mapScale: unplaced.mapScale ?? 1,
      });
    }
  }

  if (!canRead) {
    return <div className="alert alert-warning">{t('parkings.forbidden')}</div>;
  }

  const parkingColumns = [
    t('parkings.code'),
    t('parkings.name'),
    t('parkings.address'),
    t('parkings.available'),
    t('parkings.occupied'),
    t('parkings.occupancy'),
    ...(canWriteParking ? [t('common.actions')] : []),
  ];

  const spotColumns = [
    t('parkings.spotCode'),
    t('parkings.type'),
    t('parkings.status'),
    t('parkings.zone'),
    'X',
    'Y',
    ...(canWriteSpot ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWriteParking ? (
            <button type="button" className="btn btn-primary" onClick={openCreateParking}>
              {t('parkings.createParking')}
            </button>
          ) : undefined
        }
      />

      {parkings.isError && (
        <div className="alert alert-danger">{(parkings.error as Error).message}</div>
      )}

      <Card className="mb-4">
        <ParkingDataTable
          storageKey="parkings"
          columns={parkingColumns}
          refreshKey={parkingRows.map((p) => `${p.id}:${p.occupancyPct}`).join('|')}
          actionsColumn={canWriteParking}
          galleryItems={parkingRows.map((p) => ({
            id: p.id,
            icon: 'bi-p-square',
            iconTone: 'primary' as const,
            title: p.code,
            subtitle: p.name,
            meta: `${p.address}${p.city ? `, ${p.city}` : ''} · ${p.occupancyPct ?? 0}% · ${p.spotsAvailable ?? '—'} avail / ${p.spotsOccupied ?? '—'} occ`,
            actions: canWriteParking ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEditParking(p)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('parkings.confirmDeleteParking'))) {
                      deleteParking.mutate(p.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            ) : undefined,
          }))}
        >
          {parkingRows.map((p) => (
            <tr
              key={p.id}
              className={p.id === activeParkingId ? 'table-active' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedId(p.id)}
            >
              <td className="fw-semibold">{p.code}</td>
              <td>{p.name}</td>
              <td>
                {p.address}
                {p.city ? `, ${p.city}` : ''}
              </td>
              <td>{p.spotsAvailable ?? '—'}</td>
              <td>{p.spotsOccupied ?? '—'}</td>
              <td>{p.occupancyPct ?? 0}%</td>
              {canWriteParking && (
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEditParking(p)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('parkings.confirmDeleteParking'))) {
                        deleteParking.mutate(p.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <Card
        title={t('parkings.floorPlan')}
        className="mb-4 floor-plan-card"
        bodyClassName="floor-plan-card__body"
        toolbar={
          canWriteSpot ? (
            <button
              type="button"
              className="btn btn-light btn-sm"
              disabled={!activeParkingId}
              onClick={openCreateSpot}
            >
              {t('parkings.createSpot')}
            </button>
          ) : undefined
        }
      >
        <p className="text-muted small mb-3 floor-plan-card__hint">{t('parkings.floorPlanHint')}</p>
        <SpotFloorPlanCanvas
          spots={spotList}
          floorPlanUrl={activeParking?.floorPlanUrl}
          canEdit={canWriteSpot}
          emptyHint={t('parkings.floorPlanCanvasEmpty')}
          onPoseChange={(pose) => updateSpotPose.mutate(pose)}
          onPlaceUnplaced={placeUnplacedSpot}
        />
      </Card>

      <Card title={t('parkings.spots')} className="mb-4">
        <ParkingDataTable
          storageKey="parkings-spots"
          columns={spotColumns}
          refreshKey={spotList.map((s) => `${s.id}:${s.status}:${s.mapX}:${s.mapY}:${s.mapRotation}:${s.mapScale}:${s.color}`).join('|')}
          actionsColumn={canWriteSpot}
          galleryItems={spotList.map((s) => ({
            id: s.id,
            icon: 'bi-grid-3x3-gap',
            iconTone: statusToneFromEnum(s.status),
            title: s.code,
            subtitle: enumLabel(t, 'spotType', s.type),
            meta: `${s.zoneCode ?? '—'} · X:${s.mapX ?? '—'} Y:${s.mapY ?? '—'}`,
            badge: (
              <StatusBadge tone={statusToneFromEnum(s.status)}>
                {enumLabel(t, 'spotStatus', s.status)}
              </StatusBadge>
            ),
            actions: canWriteSpot ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEditSpot(s)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('parkings.confirmDeleteSpot'))) {
                      deleteSpot.mutate(s.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            ) : undefined,
          }))}
        >
          {spotList.map((s) => (
            <tr key={s.id}>
              <td className="fw-semibold">{s.code}</td>
              <td>{enumLabel(t, 'spotType', s.type)}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(s.status)}>
                  {enumLabel(t, 'spotStatus', s.status)}
                </StatusBadge>
              </td>
              <td>{s.zoneCode ?? '—'}</td>
              <td>{s.mapX ?? '—'}</td>
              <td>{s.mapY ?? '—'}</td>
              {canWriteSpot && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEditSpot(s)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('parkings.confirmDeleteSpot'))) {
                        deleteSpot.mutate(s.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      {canReadSessions && (
        <Card title={t('parkings.openSessions')} className="mb-4">
          <ParkingDataTable
            storageKey="parkings-open-sessions"
            columns={[t('parkings.plate'), t('parkings.spotCode'), t('parkings.entryAt')]}
            refreshKey={sessionRows.map((s) => s.id).join('|')}
            actionsColumn={false}
            galleryItems={sessionRows.map((s) => ({
              id: s.id,
              icon: 'bi-ticket-perforated',
              iconTone: 'info' as const,
              title: s.plate,
              subtitle: s.spotCode ?? '—',
              meta: new Date(s.entryAt).toLocaleString(),
            }))}
          >
            {sessionRows.map((s) => (
              <tr key={s.id}>
                <td className="fw-semibold">{s.plate}</td>
                <td>{s.spotCode ?? '—'}</td>
                <td>{new Date(s.entryAt).toLocaleString()}</td>
              </tr>
            ))}
          </ParkingDataTable>
        </Card>
      )}

      <AppModal
        open={parkingDialog}
        onClose={closeParkingDialog}
        title={editingParkingId ? t('parkings.editParking') : t('parkings.createParking')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeParkingDialog}>
              {t('parkings.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveParking.isPending}
              onClick={() => saveParking.mutate()}
            >
              {t('parkings.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('parkings.code')}</label>
            <input
              className="form-control"
              value={parkingForm.code}
              onChange={(e) => setParkingForm((f) => ({ ...f, code: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('parkings.name')}</label>
            <input
              className="form-control"
              value={parkingForm.name}
              onChange={(e) => setParkingForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('parkings.address')}</label>
            <input
              className="form-control"
              value={parkingForm.address}
              onChange={(e) => setParkingForm((f) => ({ ...f, address: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('parkings.city')}</label>
            <input
              className="form-control"
              value={parkingForm.city}
              onChange={(e) => setParkingForm((f) => ({ ...f, city: e.target.value }))}
            />
          </div>
          <div className="modal-form__full">
            <label className="form-label">{t('parkings.floorPlanImage')}</label>
            <input
              className="form-control"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => void onFloorPlanSelected(e)}
            />
            <div className="form-text">{t('parkings.floorPlanImageHint')}</div>
            {parkingForm.floorPlanPreview && (
              <div className="mt-3">
                <img
                  src={parkingForm.floorPlanPreview}
                  alt={t('parkings.floorPlan')}
                  className="floor-plan-preview"
                />
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() =>
                      setParkingForm((f) => ({
                        ...f,
                        floorPlanBase64: null,
                        floorPlanPreview: null,
                        clearFloorPlan: Boolean(editingParkingId),
                      }))
                    }
                  >
                    {t('parkings.floorPlanRemove')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </AppModal>

      <AppModal
        open={spotDialog}
        onClose={closeSpotDialog}
        title={editingSpotId ? t('parkings.editSpot') : t('parkings.createSpot')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeSpotDialog}>
              {t('parkings.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveSpot.isPending || (!editingSpotId && !activeParkingId)}
              onClick={() => saveSpot.mutate()}
            >
              {t('parkings.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('parkings.spotCode')}</label>
            <input
              className="form-control"
              value={spotForm.code}
              onChange={(e) => setSpotForm((f) => ({ ...f, code: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('parkings.type')}</label>
            <select
              className="form-select"
              value={spotForm.type}
              onChange={(e) => setSpotForm((f) => ({ ...f, type: e.target.value }))}
            >
              {['STANDARD', 'PMR', 'EV', 'MOTORCYCLE', 'VIP', 'SUBSCRIBER'].map((v) => (
                <option key={v} value={v}>
                  {enumLabel(t, 'spotType', v)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('parkings.status')}</label>
            <select
              className="form-select"
              value={spotForm.status}
              onChange={(e) => setSpotForm((f) => ({ ...f, status: e.target.value }))}
            >
              {['AVAILABLE', 'OCCUPIED', 'RESERVED', 'OUT_OF_SERVICE'].map((v) => (
                <option key={v} value={v}>
                  {enumLabel(t, 'spotStatus', v)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="spot-label-color">
              {t('parkings.spotLabelColor')}
            </label>
            <div className="d-flex align-items-center gap-3">
              <input
                id="spot-label-color"
                className="form-control form-control-color"
                type="color"
                value={/^#[0-9A-Fa-f]{6}$/.test(spotForm.labelColor) ? spotForm.labelColor : '#FFFFFF'}
                onChange={(e) => setSpotForm((f) => ({ ...f, labelColor: e.target.value }))}
                title={t('parkings.spotLabelColor')}
              />
              <code className="text-muted">{spotForm.labelColor}</code>
              <span
                className="spot-label-color-preview"
                style={{ color: spotForm.labelColor, background: '#2b2b40' }}
              >
                {spotForm.code.trim() || 'A-01'}
              </span>
            </div>
            <div className="form-text">{t('parkings.spotLabelColorHint')}</div>
          </div>
        </div>
      </AppModal>
    </>
  );
}
